"use client";

import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Download, User } from "lucide-react";
import { exportToExcel } from "@/lib/excelExport";
import { ShortId } from "@/components/ui/short-id";
import { format } from "date-fns";
import { FeatureUsageRow } from "@/services/reportsService";

interface CustomerWashHistoryDashboardProps {
  data: {
    customerId: string;
    customerName: string;
    dateRange: {
      start: string;
      end: string;
    };
    history: Array<{
      cleanDate: string;
      serviceType: string;
      status: string;
      featuresUsed: string[];
      washCount: number;
    }>;
    totalWashes: number;
    totalAppointments: number;
    featureSummary: FeatureUsageRow[];
  };
}

const ALL_FEATURES = "all";

function describeAllowance(feature: FeatureUsageRow) {
  if (feature.allowance === null) {
    return "Included in plan";
  }
  return `Allowance ${feature.allowance} · ${feature.remaining ?? 0} left`;
}

export function CustomerWashHistoryDashboard({
  data,
}: CustomerWashHistoryDashboardProps) {
  const [featureFilter, setFeatureFilter] = useState(ALL_FEATURES);

  // Every feature that appears in the plan summary or on an appointment
  const featureNames = Array.from(
    new Set([
      ...data.featureSummary.map((feature) => feature.featureName),
      ...data.history.flatMap((row) => row.featuresUsed),
    ]),
  );

  const filteredHistory =
    featureFilter === ALL_FEATURES
      ? data.history
      : data.history.filter((row) => row.featuresUsed.includes(featureFilter));

  const countFeatureUse = (featureName: string) =>
    filteredHistory.filter((row) => row.featuresUsed.includes(featureName))
      .length;

  const handleExport = () => {
    const exportData = filteredHistory.map((row) => {
      const rowData: Record<string, string | number> = {
        "Clean Date": format(new Date(row.cleanDate), "dd/MM/yyyy"),
        "Customer Name": data.customerName,
        "Service Type": row.serviceType,
        Status: row.status,
      };
      featureNames.forEach((name) => {
        rowData[name] = row.featuresUsed.includes(name) ? "Yes" : "-";
      });
      rowData["No. Wash"] = row.washCount;
      return rowData;
    });

    // Add totals row
    const totalsRow: Record<string, string | number> = {
      "Clean Date": "TOTAL",
      "Customer Name": data.customerName,
      "Service Type": "-",
      Status: "-",
    };
    featureNames.forEach((name) => {
      totalsRow[name] = countFeatureUse(name);
    });
    totalsRow["No. Wash"] = filteredHistory.length;
    exportData.push(totalsRow);

    exportToExcel(
      exportData,
      `Wash_History_${data.customerName.replace(/\s+/g, "_")}`,
      "Wash History"
    );
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status.toUpperCase()) {
      case "COMPLETED":
      case "FINISHED":
        return "default";
      case "CANCELLED":
        return "destructive";
      case "IN_PROGRESS":
        return "secondary";
      default:
        return "outline";
    }
  };

  return (
    <div className="space-y-4">
      {/* Customer Info Card */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950 dark:to-purple-950">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            {data.customerName}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <p className="text-sm text-muted-foreground">Customer ID</p>
              <div className="mt-1">
                <ShortId id={data.customerId} label="Customer ID" />
              </div>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Period</p>
              <p className="text-lg font-medium">
                {format(new Date(data.dateRange.start), "dd MMM")} -{" "}
                {format(new Date(data.dateRange.end), "dd MMM yyyy")}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Completed Washes</p>
              <p className="text-3xl font-bold text-blue-600">
                {data.totalWashes}
              </p>
              <p className="text-xs text-muted-foreground">
                of {data.totalAppointments} appointments
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Feature Usage (subscription features used in this period) */}
      {data.featureSummary.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Feature Usage</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {data.featureSummary.map((feature) => (
                <div
                  key={feature.featureId}
                  className="rounded-lg border p-4"
                >
                  <p className="text-sm text-muted-foreground">
                    {feature.featureName}
                  </p>
                  <p className="text-2xl font-bold">
                    {feature.used}{" "}
                    <span className="text-sm font-normal text-muted-foreground">
                      used
                    </span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {describeAllowance(feature)}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Wash History Table */}
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
          <CardTitle>Wash History</CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={featureFilter} onValueChange={setFeatureFilter}>
              <SelectTrigger className="w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_FEATURES}>All Features</SelectItem>
                {featureNames.map((name) => (
                  <SelectItem key={name} value={name}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={handleExport}>
              <Download className="h-4 w-4 mr-2" />
              Export to Excel
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Clean Date</TableHead>
                <TableHead>Service Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Features Used</TableHead>
                <TableHead className="text-right">No. Wash</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredHistory.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    No wash history found for this customer
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {filteredHistory.map((record, index) => (
                    <TableRow key={`${record.cleanDate}-${index}`}>
                      <TableCell>
                        {format(new Date(record.cleanDate), "dd MMM yyyy")}
                      </TableCell>
                      <TableCell>{record.serviceType}</TableCell>
                      <TableCell>
                        <Badge variant={getStatusBadgeVariant(record.status)}>
                          {record.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {record.featuresUsed.length === 0 ? (
                          <span className="text-muted-foreground">-</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {record.featuresUsed.map((name) => (
                              <Badge key={name} variant="secondary">
                                {name}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {record.washCount}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-muted/50 font-bold">
                    <TableCell colSpan={4}>TOTAL</TableCell>
                    <TableCell className="text-right">
                      {filteredHistory.length}
                    </TableCell>
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
